"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.emptyDataViewForResult = exports.DataViewFacade = void 0;
// (C) 2019-2021 GoodData Corporation
var sdk_model_1 = require("@gooddata/sdk-model");
var definitionMethods_1 = require("./internal/definitionMethods");
var resultMetaMethods_1 = require("./internal/resultMetaMethods");
var resultDataMethods_1 = require("./internal/resultDataMethods");
var dataAccessMethods_1 = require("./internal/dataAccessMethods");
/**
 * This wrapper for {@link @gooddata/sdk-backend-spi#IDataView} provides various convenience methods to work with data and metadata stored inside
 * the provided instance of {@link @gooddata/sdk-backend-spi#IDataView}.
 *
 * The facade keeps an ephemeral state - such as calculated indexes on top of the headers in the {@link @gooddata/sdk-backend-spi#IDataView} -
 * to optimize performance of often-used lookups at the cost of extra memory.
 *
 * The facade is part of the public API and we strongly recommend to use it whenever client code needs to work with
 * data view; ideally, single instance of data view facade
 *
 * Note: the facade is currently in alpha quality - mix-match of various functions we found useful so far; consolidation
 * and further enhancements will happen, the methods will be removed, renamed and added in the future. The public
 * API WILL break.
 *
 * @public
 */
var DataViewFacade = /** @class */ (function () {
    function DataViewFacade(dataView) {
        this.dataView = dataView;
        this.definition = dataView.definition;
    }
    //
    // Own methods
    //
    /**
     * @param dataView - instance of data view to create the facade for
     * @public
     */
    DataViewFacade.for = function (dataView) {
        if (!DataViewFacade.Facades.has(dataView)) {
            DataViewFacade.Facades.set(dataView, new DataViewFacade(dataView));
        }
        return DataViewFacade.Facades.get(dataView);
    };
    /**
     * Creates a DataViewFacade with provided execution result. Only use this when execution result is unable to load data and some
     * form of DataViewFacade is still needed. Beware that the calculated data view is empty after the creation. Only execution
     * definition and result is defined.
     *
     * @param result - instance of execution result to create the facade for
     * @public
     */
    DataViewFacade.forResult = function (result) {
        if (!DataViewFacade.FacadesForResult.has(result)) {
            var emptyView = emptyDataViewForResult(result);
            DataViewFacade.FacadesForResult.set(result, new DataViewFacade(emptyView));
        }
        return DataViewFacade.FacadesForResult.get(result);
    };
    /**
     * @returns result of execution which returned this data view
     * @public
     */
    DataViewFacade.prototype.result = function () {
        return this.dataView.result;
    };
    /**
     * @returns execution result warnings
     * @public
     */
    DataViewFacade.prototype.warnings = function () {
        var _a;
        return (_a = this.dataView.warnings) !== null && _a !== void 0 ? _a : [];
    };
    /**
     * @remarks see {@link @gooddata/sdk-backend-spi#IDataView.fingerprint} for more contractual information
     * @returns fingerprint of the data view
     * @public
     */
    DataViewFacade.prototype.fingerprint = function () {
        return this.dataView.fingerprint();
    };
    /**
     * @returns methods to access data in a curated fashion using data slices and data series iterators
     * @public
     */
    DataViewFacade.prototype.data = function (config) {
        if (!this.dataAccessMethods) {
            this.dataAccessMethods = dataAccessMethods_1.newDataAccessMethods(this.dataView, config);
        }
        return this.dataAccessMethods;
    };
    //
    //
    //
    /**
     * @returns methods to work with execution definition
     * @internal
     */
    DataViewFacade.prototype.def = function () {
        if (!this.definitionMethods) {
            this.definitionMethods = definitionMethods_1.newExecutionDefinitonMethods(this.dataView.definition);
        }
        return this.definitionMethods;
    };
    /**
     * @returns methods to work with result metadata
     * @internal
     */
    DataViewFacade.prototype.meta = function () {
        if (!this.resultMetaMethods) {
            this.resultMetaMethods = resultMetaMethods_1.newResultMetaMethods(this.dataView);
        }
        return this.resultMetaMethods;
    };
    /**
     * @returns methods to work with the raw data included in the result
     * @internal
     */
    DataViewFacade.prototype.rawData = function () {
        if (!this.resultDataMethods) {
            this.resultDataMethods = resultDataMethods_1.newResultDataMethods(this.dataView);
        }
        return this.resultDataMethods;
    };
    DataViewFacade.Facades = new WeakMap();
    DataViewFacade.FacadesForResult = new WeakMap();
    return DataViewFacade;
}());
exports.DataViewFacade = DataViewFacade;
/**
 * Constructs an empty data view with given execution result.
 *
 * @param result - execution result
 * @returns data view
 * @public
 */
function emptyDataViewForResult(result) {
    var definition = result.definition;
    var fp = sdk_model_1.defFingerprint(definition) + "/emptyView";
    return {
        definition: definition,
        result: result,
        headerItems: [],
        data: [],
        offset: [0, 0],
        count: [0, 0],
        totalCount: [0, 0],
        fingerprint: function () {
            return fp;
        },
        equals: function (other) {
            return fp === other.fingerprint();
        },
    };
}
exports.emptyDataViewForResult = emptyDataViewForResult;
//# sourceMappingURL=facade.js.map